/*
 * DOLLAR wizard input component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { Constants } from "app/shared/config/constants";

@Component ( {
  selector: 'reporter-dollar',
  templateUrl: './dollar.component.html',
  styleUrls: [ './dollar.component.scss' ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef ( () => DollarComponent ),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef ( () => DollarComponent ),
      multi: true
    },
  ],
  standalone: false
} )

export class DollarComponent implements ControlValueAccessor, Validator, AfterViewInit, OnChanges {

  @Input () isWage: boolean;
  @Input () maskWage = false;
  @Input () masked = false;

  maskedWageValue = '';
  maskedValue: string | null = null;

  constructor (
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {
    this.maskedWageValue = Constants.maskedWageValue;
  }

  set value ( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value () {
    return this.innerValue;
  }

  get required (): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;
  initialValue: any;

  @ViewChild ( 'field', { static: false } ) field1: ElementRef;
  @ViewChild ( 'fieldMasked', { static: false } ) field2: ElementRef;

  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };
  onValidatorChange = () => {
  };

  ngOnChanges ( changes: SimpleChanges ) {
    this.syncMaskedValue ();

    if ( changes.isWage || changes.maskWage || changes.masked ) {
      this.onValidatorChange ();
    }
  }

  ngAfterViewInit () {
    this.initialValue = this.value;
    this.syncMaskedValue ();
    this.initialized = true;
    this.onValidatorChange ();
  }

  writeValue ( value: any ): void {
    if ( value === this.maskedWageValue ) {
      this.innerValue = value;
      this.syncMaskedValue ();
      return;
    }

    let newVal = value;
    if ( value === null || value === undefined ) {
      newVal = undefined;
    } else {
      if ( newVal > 99999999.99 ) {
        newVal = parseInt ( `${ newVal * 100 }`.substring ( 0, 9 ), 10 ) / 100;
      }

      if ( `${ value }`.match ( /\.\d{2}/g ) ) {
        newVal = parseFloat ( value )
          .toFixed ( 2 );
      } else if ( `${ value }`.match ( /\.\d{1}/g ) ) {
        newVal = parseFloat ( value )
          .toFixed ( 1 );
      } else {
        newVal = parseInt ( value, 10 );
      }
    }

    this.innerValue = newVal;
    this.syncMaskedValue ();
    this.renderer.setProperty ( this.elementRef.nativeElement, 'value', newVal );
    if ( this.field ) {
      this.renderer.setProperty ( this.field.nativeElement, 'value', newVal );
    }

    this.onChange ( newVal );
    this.onTouched ();
  }

  registerOnChange ( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched ( fn: () => any ): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange ( fn: () => void ): void {
    this.onValidatorChange = fn;
  }

  setDisabledState ( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate ( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }

    if ( ( control.value === null || control.value === undefined ) && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }

    if ( this.isMaskedDisplayValue ( control.value ) ) {
      return null;
    }

    if ( control.value !== undefined && control.value !== null && control.value !== this.initialValue ) {
      if ( control.value !== undefined && isNaN ( control.value ) ) {
        return {
          badInput: true,
          message: ' must be a correct decimal number.'
        };
      }
      if ( this.value < -99999999.99 ) {
        return {
          rangeUnderflow: true,
          message: ' must be greater than -99999999.99.'
        };
      }
      if ( this.value > 99999999.99 ) {
        return {
          rangeOverflow: true,
          message: ' must be less than 99999999.99.'
        };
      }
    }

    return null;
  }

  get field (): ElementRef {
    return this.field1 || this.field2;
  }

  get showMaskedField (): boolean {
    return this.maskWage && this.isWage && this.masked && this.hasValue ( this.innerValue );
  }

  get numericInputValue (): any {
    return this.showMaskedField && this.innerValue === this.maskedWageValue
      ? undefined
      : this.innerValue;
  }

  maskedFocus( $event: any ): void {
    $event.target.select ();
  }

  maskedChange( $event: string ): void {
    const field = this.field1.nativeElement;
    this.masked = false;
    this.maskedValue = null;
    field.value = $event;
    this.onValidatorChange ();
    this.onChange ( $event );
    setTimeout ( () => field.focus (), 0 );
  }

  private syncMaskedValue (): void {
    this.maskedValue = this.showMaskedField ? this.maskedWageValue : null;
  }

  private isMaskedDisplayValue ( value: any ): boolean {
    return this.maskWage && this.isWage && ( value === this.maskedWageValue || ( this.masked && value === this.initialValue ) );
  }

  private hasValue ( value: any ): boolean {
    return value !== undefined && value !== null && value !== '';
  }
}
